import type Fuse from "fuse.js";
import type { IFuseOptions } from "fuse.js";
import { assistantSessionsPath, buildSessionsScopeParam } from "@/lib/assistant-sessions";
import type { NamedOrg } from "@/lib/named-org";
import type { AxiosInstance } from "axios";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { toast } from "sonner";
import { useDebounceValue } from "usehooks-ts";
import { useApi } from "./useApi";
import { useOrgFromPathname } from "./useOrgFromPathname";
import { orgInstanceSlug, useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
import type { CachedAssistantSession, SearchedSession } from "./useAssistantSessionCache";
const MIN_QUERY_LENGTH = 2;
// The semantic endpoint validates q as min:3.
const MIN_SEMANTIC_QUERY_LENGTH = 3;
const MAX_SEARCH_RESULTS = 8;
const SEARCH_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const PAGE_SIZE = 500;
const MAX_SESSIONS_PER_POOL = 2000;
const TEXT_SEARCH_DEBOUNCE_MS = 150;
// Each semantic query costs an embeddings call and the endpoint allows 30/min.
const SEMANTIC_SEARCH_DEBOUNCE_MS = 400;
// Relevance runs from 0.7 (threshold) to 1 (exact); a full window of recency
// is worth 0.2, about one typo in a five-letter word, so a clean match from
// last week beats a one-typo match from today.
const RECENCY_WEIGHT = 0.2;
const FUSE_OPTIONS = {
  keys: ["title"],
  threshold: 0.3,
  ignoreLocation: true,
  ignoreFieldNorm: true,
  includeScore: true
} satisfies IFuseOptions<CachedAssistantSession>;
type ContentMatch = CachedAssistantSession & {
  snippet?: string;
};

// Mirrors sessionUserActivityAt in components/codesmith/utils, inlined because
// that module pulls zod into the shared bundle that hosts the palette (which is
// also why fuse.js loads lazily).
function userActivityMs(session: CachedAssistantSession): number {
  return new Date(session.last_user_message_at || session.created_at).getTime();
}

// Pages arrive pinned rows first, then by last user activity, so the window
// ends at the first page whose last unpinned row predates it.
async function fetchSessionsActiveSince(api: AxiosInstance, url: string, cutoffMs: number): Promise<CachedAssistantSession[]> {
  const sessions: CachedAssistantSession[] = [];
  let offset = 0;
  while (sessions.length < MAX_SESSIONS_PER_POOL) {
    const response = await api.get(`${url}&offset=${offset}`);
    const page = (response.data.sessions ?? []) as CachedAssistantSession[];
    sessions.push(...page);
    const last = page.at(-1);
    const pastWindow = !!last && !last.pinned && userActivityMs(last) < cutoffMs;
    if (page.length < PAGE_SIZE || pastWindow) break;
    offset = response.data.next_offset;
  }
  return sessions.filter(session => userActivityMs(session) >= cutoffMs);
}

// Scored per term rather than with Fuse's $and, which multiplies term scores
// so one exact term hides a sloppy one. A session's relevance is its worst
// term, plus a recency boost.
function rankTitleMatches(fuse: Fuse<CachedAssistantSession>, query: string, indexedAtMs: number): CachedAssistantSession[] {
  const terms = query.split(/\s+/);
  const [firstTermMatches, ...otherTermMatches] = terms.map(term => fuse.search(term));
  const otherTermScores = otherTermMatches.map(matches => new Map(matches.map(({
    item,
    score = 1
  }) => [item.id, score])));
  const scored = firstTermMatches.flatMap(({
    item,
    score = 1
  }) => {
    let worstScore = score;
    for (const scores of otherTermScores) {
      const termScore = scores.get(item.id);
      if (termScore === undefined) return [];
      worstScore = Math.max(worstScore, termScore);
    }
    const ageMs = Math.max(0, indexedAtMs - userActivityMs(item));
    const recency = Math.max(0, 1 - ageMs / SEARCH_WINDOW_MS);
    return [{
      item,
      rank: 1 - worstScore + RECENCY_WEIGHT * recency
    }];
  });
  return scored.sort((a, b) => b.rank - a.rank).map(({
    item
  }) => item);
}

// Title and managed PR branch/title/repo matches of any age. The palette
// shows rows only, so the list endpoint skips its three scope-wide counts.
async function fetchTextMatches(api: AxiosInstance, org: NamedOrg, term: string): Promise<ContentMatch[]> {
  const base = assistantSessionsPath(org);
  const scope = buildSessionsScopeParam(null, org);
  const query = encodeURIComponent(term);
  const response = await api.get(`${base}?status=active&limit=${MAX_SEARCH_RESULTS}&without_totals=1&search=${query}${scope}`);
  return (response.data.sessions ?? []) as ContentMatch[];
}

// Transcript passages matched by meaning, each with its snippet.
async function fetchSemanticMatches(api: AxiosInstance, org: NamedOrg, term: string): Promise<ContentMatch[]> {
  const base = assistantSessionsPath(org);
  const scope = buildSessionsScopeParam(null, org);
  const query = encodeURIComponent(term);
  const response = await api.get(`${base}/semantic-search?q=${query}&limit=${MAX_SEARCH_RESULTS}${scope}`);
  return (response.data.sessions ?? []) as ContentMatch[];
}

/**
 * Session search for the palette. Titles from the last two weeks match
 * instantly from a window loaded when the palette opens; the typed phrase is
 * then searched server-side, debounced, across every session's title, PR
 * fields and transcript. Own sessions rank above team sessions.
 */
export function useAssistantSessionSearch(searchTerm: string, userId?: number, enabled = true) {
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  const installation = connectedGithubOrgs?.installations.find(candidate => candidate?.account?.login === activeGithubOrg);
  const codesmithEnabled = installation?.codesmith_enabled ?? false;
  const semanticEnabled = installation?.codesmith_session_search_enabled ?? false;
  const instanceSlug = orgInstanceSlug(connectedGithubOrgs, activeGithubOrg ?? null);
  const searchable = !!activeGithubOrg && !!instanceSlug && codesmithEnabled;
  const onError = () => toast.error("Failed to search sessions", {
    id: "assistant-session-search"
  });
  const {
    data: fuse,
    dataUpdatedAt: indexedAtMs,
    isLoading: indexLoading
  } = useQuery<Fuse<CachedAssistantSession>>(["assistantSessionSearchIndex", instanceSlug, activeGithubOrg], async () => {
    const org = {
      orgName: activeGithubOrg!,
      instanceSlug: instanceSlug!
    };
    const url = `${assistantSessionsPath(org)}?status=active&limit=${PAGE_SIZE}${buildSessionsScopeParam(null, org)}`;
    const cutoffMs = Date.now() - SEARCH_WINDOW_MS;
    // The user's own pool is fetched separately so an admin's busy team
    // can't push their sessions past the per-pool cap.
    const [pools, {
      default: FuseIndex
    }] = await Promise.all([Promise.all([fetchSessionsActiveSince(api, `${url}&ownership=mine`, cutoffMs), fetchSessionsActiveSince(api, url, cutoffMs)]), import("fuse.js")]);
    // Offset pages over a moving activity order can repeat a row.
    const byId = new Map(pools.flat().map(session => [session.id, session]));
    return new FuseIndex([...byId.values()], FUSE_OPTIONS);
  }, {
    enabled: enabled && searchable,
    // Refetch on every palette open so new, renamed and archived sessions
    // show up; the cached window still answers while it loads.
    staleTime: 0,
    refetchOnWindowFocus: false,
    onError
  });
  const trimmed = searchTerm.trim();
  const queryActive = searchable && trimmed.length >= MIN_QUERY_LENGTH;
  const org_0 = {
    orgName: activeGithubOrg!,
    instanceSlug: instanceSlug!
  };

  // Text and transcript searches run independently so text rows render as
  // soon as they land instead of waiting on the embeddings round trip.
  const [textTerm] = useDebounceValue(trimmed, TEXT_SEARCH_DEBOUNCE_MS);
  const {
    data: textMatches,
    isFetching: textFetching
  } = useQuery<ContentMatch[]>(["assistantSessionTextSearch", instanceSlug, activeGithubOrg, textTerm], () => fetchTextMatches(api, org_0, textTerm), {
    enabled: enabled && searchable && textTerm.length >= MIN_QUERY_LENGTH,
    // A retry would hold the "Searching" row for seconds; the next
    // keystroke re-queries anyway.
    retry: false,
    refetchOnWindowFocus: false,
    onError
  });
  const textPending = queryActive && (textTerm !== trimmed || textFetching);
  const semanticActive = semanticEnabled && trimmed.length >= MIN_SEMANTIC_QUERY_LENGTH;
  const [semanticTerm] = useDebounceValue(trimmed, SEMANTIC_SEARCH_DEBOUNCE_MS);
  const {
    data: semanticMatches,
    isFetching: semanticFetching
  } = useQuery<ContentMatch[]>(["assistantSessionSemanticSearch", instanceSlug, activeGithubOrg, semanticTerm], () => fetchSemanticMatches(api, org_0, semanticTerm), {
    enabled: enabled && searchable && semanticEnabled && semanticTerm.length >= MIN_SEMANTIC_QUERY_LENGTH,
    // Transcript matches are a bonus: a failure (e.g. the 30/min limit)
    // leaves the title and text rows alone, and a retry would only spend
    // more of that limit.
    retry: false,
    refetchOnWindowFocus: false,
    onError: () => {}
  });
  const semanticPending = queryActive && semanticActive && (semanticTerm !== trimmed || semanticFetching);
  const sessions = useMemo<SearchedSession[]>(() => {
    if (!queryActive) return [];
    const titleMatches: ContentMatch[] = fuse ? rankTitleMatches(fuse, trimmed, indexedAtMs) : [];

    // Rows land in three waves (title, text, transcript) and a mounted row
    // must not change under the cursor, so the first wave to list a session
    // owns it; only rows the transcript wave introduces carry a snippet.
    // semanticMatches can outlive a query that shrank below the semantic
    // minimum until its debounce catches up.
    const byId_0 = new Map<string, ContentMatch>();
    for (const match of [...titleMatches, ...(textMatches ?? []), ...(semanticActive ? semanticMatches ?? [] : [])]) {
      if (!byId_0.has(match.id)) byId_0.set(match.id, match);
    }
    const ranked = [...byId_0.values()].map(session_0 => ({
      ...session_0,
      isMine: userId != null && session_0.user_id === userId
    }));
    const mine = ranked.filter(session_1 => session_1.isMine);
    const team = ranked.filter(session_2 => !session_2.isMine);
    return [...mine, ...team].slice(0, MAX_SEARCH_RESULTS);
  }, [fuse, indexedAtMs, queryActive, semanticActive, semanticMatches, textMatches, trimmed, userId]);
  return {
    sessions,
    isLoading: enabled && queryActive && (indexLoading || textPending || semanticPending)
  };
}